import { MONS } from '../mock/data.ts';
import type { SpeciesId, SpeciesParse } from '../types.ts';
import { Sprite } from './Sprite.tsx';

// Species-parser block — when the user types comma-separated species, the
// agent confirms each parse before running predict_opponent_leads. Each
// entry shows the raw user input + the resolved species + a ✓/✗ pill.
//
// Visual cue: "I read this correctly" → user can correct typos before tool
// calls fire. Per design handoff mockup 02 flow step 3. When the raw input
// differs from the resolved canonical name (e.g. "char y" → "Charizard-Mega-Y"
// after parsing) we show BOTH so the user can spot a wrong fuzzy match.
export function SpeciesParserCard({ data }: { data: SpeciesParse | undefined }) {
  if (!data || !Array.isArray(data.parsed) || data.parsed.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        padding: '8px 10px',
        background: 'var(--bg-2)',
        border: '1px solid var(--line-1)',
        borderRadius: 4,
      }}
      aria-label="Parsed opponent species"
    >
      {data.parsed.map((entry, i) => {
        const ok = entry.resolved !== null;
        const mon = ok ? MONS[entry.resolved as SpeciesId] : null;
        const canonicalName = mon?.name ?? entry.resolved ?? '';
        // Show the raw token when it doesn't already match the canonical name
        // (case-insensitive substring check). The fuzzy normalization happens
        // server-side; the user needs to see what they typed when it doesn't.
        const rawDiffers = ok && !canonicalName.toLowerCase().includes(entry.raw.toLowerCase());
        return (
          <span
            key={`${entry.raw}::${entry.resolved ?? 'unresolved'}::${i}`}
            aria-label={
              ok
                ? `Recognized ${canonicalName}${rawDiffers ? ` (from "${entry.raw}")` : ''}`
                : `Did not recognize "${entry.raw}"`
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 7px',
              background: ok ? 'rgba(136,176,122,0.10)' : 'rgba(201,119,102,0.10)',
              border: `1px solid ${ok ? 'rgba(136,176,122,0.40)' : 'rgba(201,119,102,0.40)'}`,
              borderRadius: 2,
              fontSize: 11,
              color: ok ? 'var(--fg-1)' : 'var(--fg-1)',
            }}
          >
            <span
              className="mono"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: ok ? 'var(--pos)' : 'var(--neg)',
              }}
              aria-hidden="true"
            >
              {ok ? '✓' : '✗'}
            </span>
            {ok && entry.resolved && <Sprite species={entry.resolved} size={18} />}
            <span>{ok ? canonicalName : entry.raw}</span>
            {rawDiffers ? (
              <span
                className="mono"
                style={{ fontSize: 9.5, color: 'var(--fg-3)' }}
                title={`Resolved from "${entry.raw}"`}
              >
                ← "{entry.raw}"
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
